import type { Address } from '@nvd/claim-shared';
import { useState } from 'react';
import { AddressForm, EMPTY_ADDRESS, isAddressComplete } from '../components/AddressForm';
import { ClaimedItems } from '../components/ClaimedItems';
import { StepLayout } from '../components/StepLayout';
import { UI } from '../lib/ui';
import { useAction, usePortal } from '../Portal';
import { buildSubmitRequest } from '../state/machine';

function addressLines(address: Address): string[] {
  const cityLine = [address.city, address.province, address.zip].filter(Boolean).join(', ');
  return [
    [address.firstName, address.lastName].filter(Boolean).join(' '),
    address.address1,
    address.address2,
    cityLine,
    address.country || address.countryCode,
    address.phone,
  ].filter((line) => line.trim() !== '');
}

export function ReorderConfirm() {
  const { config, t, api, state, dispatch } = usePortal();
  const resolution = state.resolution;
  const [address, setAddress] = useState<Address>(state.reorderAddress ?? EMPTY_ADDRESS);
  // A complete address on file shows as a summary; the form only opens when it needs work.
  const [editing, setEditing] = useState(() => !isAddressComplete(address) && config.intake.editShippingAddress);
  const { busy, error, setError, run } = useAction();

  const submit = () => {
    if (!resolution) return;
    if (!isAddressComplete(address)) {
      setEditing(true);
      setError(UI.addressInvalid);
      return;
    }
    void run(async () => {
      const { outcome } = await api.submit(buildSubmitRequest(state.draft, resolution.id, address));
      dispatch({ type: 'SUBMITTED', outcome });
    });
  };

  if (!resolution) return null;

  return (
    <StepLayout
      title={state.autoReorder ? t('reorder.title') : t('reorder.manualTitle')}
      paragraph={state.autoReorder ? t('reorder.body') : t('reorder.manualBody')}
      error={error}
      busy={busy}
      onBack={() => dispatch({ type: 'BACK' })}
      onNext={submit}
      nextLabel={state.autoReorder ? t('reorder.submit') : t('common.submit')}
    >
      <ClaimedItems />
      {editing ? (
        <AddressForm value={address} onChange={setAddress} />
      ) : (
        <div className="nvd-claim-portal__address-card">
          <div>
            <p className="nvd-claim-portal__label">{t('reorder.shipToLabel')}</p>
            <address className="nvd-claim-portal__address-lines">
              {addressLines(address).map((line) => (
                <span key={line}>{line}</span>
              ))}
            </address>
          </div>
          {config.intake.editShippingAddress && (
          <button type="button" className="nvd-claim-portal__link" onClick={() => setEditing(true)}>
            {t('reorder.edit')}
          </button>
          )}
        </div>
      )}
    </StepLayout>
  );
}
